"use client";

import { useState } from "react";
import Image from "next/image";
import { storyblokEditable, type SbBlokData } from "@storyblok/react/rsc";

import { resolveBg } from "@/lib/bg-color";
import { useMediaQuery } from "@/lib/use-media-query";

type Asset = { filename?: string; alt?: string };

type AccordionLogoItem = SbBlokData & {
  logo?: Asset;
  title?: string;
  description?: string;
};

type AccordionListWithLogoBlok = SbBlokData & {
  title?: string;
  items?: AccordionLogoItem[];
  bg_color?: unknown;
};

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      className={`transition-transform duration-200 ease-out ${open ? "rotate-90" : ""}`}
    >
      <path
        d="M7.5 5l5 5-5 5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function AccordionListWithLogo({ blok }: { blok: AccordionListWithLogoBlok }) {
  const items = blok.items || [];
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const { bg, onDark } = resolveBg(blok.bg_color, "dusk-01");

  const isDesktop = useMediaQuery("(min-width: 768px)");

  if (items.length === 0) return null;

  return (
    <section {...storyblokEditable(blok)} className={bg}>
      <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] py-[clamp(2.5rem,5vw,5rem)]">
        {blok.title && (
          <h2
            className={`text-[clamp(2.25rem,4vw,3rem)] max-w-[820px] leading-[1.08] font-bold ${
              onDark ? "text-white" : "text-purple-05"
            }`}
          >
            {blok.title}
          </h2>
        )}

        <ul className="mt-8 flex flex-col gap-4 md:gap-6 lg:mt-12">
          {items.map((item, i) => {
            const open = openIndex === i;
            const hasDescription = Boolean(item.description);
            const logo = item.logo?.filename ? (
              <Image
                src={item.logo.filename}
                alt={item.logo.alt || ""}
                width={360}
                height={360}
                className={
                  isDesktop
                    ? "size-[130px] shrink-0 object-contain lg:size-[160px]"
                    : "size-16 shrink-0 object-contain"
                }
              />
            ) : null;

            return (
              <li
                key={item._uid}
                {...storyblokEditable(item)}
                className="rounded-[16px] bg-white p-[clamp(1.5rem,3vw,2.5rem)]"
              >
                {isDesktop ? (
                  <div className="flex items-center gap-[clamp(1.5rem,3vw,3rem)]">
                    {logo}
                    <div className="flex min-w-0 flex-1 flex-col gap-3">
                      <span className="text-h5 font-bold leading-[1.12] text-black">
                        {item.title}
                      </span>
                      {hasDescription && (
                        <p className="text-h6 leading-[1.56] text-black/75">
                          {item.description}
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => hasDescription && setOpenIndex(open ? null : i)}
                      aria-expanded={hasDescription ? open : undefined}
                      disabled={!hasDescription}
                      className="flex w-full items-center gap-4 text-left"
                    >
                      {logo}
                      <span className="flex-1 text-h6 font-bold leading-[1.12] text-black">
                        {item.title}
                      </span>
                      {hasDescription && (
                        <span className="text-purple-03 flex size-7 shrink-0 items-center justify-center">
                          <ChevronIcon open={open} />
                        </span>
                      )}
                    </button>

                    {hasDescription && (
                      <div
                        inert={!open}
                        className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                        }`}
                      >
                        <div className="overflow-hidden">
                          <p className="pt-4 text-body leading-[1.56] text-black/75">
                            {item.description}
                          </p>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
